import React from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/logo.png";

const startFreeTrialTo = "/register";

const plans = [
  {
    name: "Starter",
    price: "$99/mo",
    subtitle: "For solo operators getting traction",
    features: [
      "Up to 50 clients",
      "2 team seats (1 admin + 1 staff)",
      "Basic branding (logo only)",
      "Core dispute tools",
      "Email support",
    ],
  },
  {
    name: "Growth",
    price: "$199/mo",
    subtitle: "For teams scaling volume",
    features: [
      "Up to 200 clients",
      "6 team seats",
      "White-label branding (custom colors, logo, portal branding)",
      "AI-powered dispute reasons",
      "Advanced automation",
      "Priority support",
    ],
    featured: true,
  },
  {
    name: "Agency",
    price: "$399/mo",
    subtitle: "For high-volume agencies",
    features: [
      "Unlimited clients",
      "Unlimited team seats",
      "Full white-label with custom domain",
      "Deep analytics & reporting",
      "Dedicated onboarding",
      "Custom integrations",
    ],
  },
];

const PricingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-950/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="IntelliWarrior" className="h-8 w-8 rounded-full" />
            <span className="text-lg font-semibold">IntelliWarrior</span>
          </Link>
          <div className="flex gap-4 text-sm text-slate-300">
            <Link to="/features" className="hover:text-cyan-300">
              Features
            </Link>
            <Link to="/login" className="hover:text-cyan-300">
              Login
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-white">Pricing built for every growth stage</h1>
        <p className="mt-3 max-w-2xl text-slate-300">
          Clear monthly plans with no hidden platform fees. Start with the tier that matches your current capacity and upgrade as your pipeline grows.
        </p>

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {plans.map((plan) => (
            <section
              key={plan.name}
              className={`relative rounded-2xl border p-6 ${
                plan.featured
                  ? "border-cyan-400/60 bg-gradient-to-b from-cyan-500/10 to-slate-900 shadow-xl shadow-cyan-900/40"
                  : "border-slate-800 bg-slate-900/80"
              }`}
            >
              {plan.featured ? (
                <span className="absolute right-4 top-4 rounded-full bg-cyan-500/20 px-2.5 py-0.5 text-xs font-semibold text-cyan-200">
                  Most Popular
                </span>
              ) : null}
              <h2 className="text-2xl font-semibold text-white">{plan.name}</h2>
              <p className="mt-1 text-sm text-slate-400">{plan.subtitle}</p>
              <p className="mt-4 text-3xl font-bold text-cyan-200">{plan.price}</p>
              <p className="mt-1 text-sm text-emerald-400">
                {"14-day free trial \u00b7 Cancel anytime"}
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-300">
                {plan.features.map((feature) => (
                  <li key={feature}>
                    {"\u2022 "}
                    {feature}
                  </li>
                ))}
              </ul>
              <Link
                to={startFreeTrialTo}
                className="mt-6 inline-block rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-400"
              >
                Start Free Trial
              </Link>
            </section>
          ))}
        </div>

        <section className="mt-12 rounded-xl border border-slate-800 bg-slate-900/60 p-6">
          <h3 className="text-lg font-semibold text-white">Need custom enterprise support?</h3>
          <p className="mt-2 text-sm text-slate-300">
            If you need migration assistance, custom integrations, or advanced onboarding, contact us and we will tailor a package for your agency.
          </p>
          <a
            href="mailto:info@intelliwarrior.com?subject=IntelliWarrior%20Enterprise%20Inquiry"
            className="mt-4 inline-block text-sm font-semibold text-cyan-300 hover:text-cyan-200"
          >
            Talk to sales
          </a>
        </section>
      </main>
    </div>
  );
};

export default PricingPage;
